import { Button } from "@/components/ui/button";
import { ArrowRight, Play } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import heroImage from "@/assets/hero-motivation.jpg";

const Hero = () => {
  const { t } = useLanguage();
  
  const scrollToFunnel = () => {
    document.getElementById('funnel')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden w-full pt-16 sm:pt-20 md:pt-24 lg:pt-0">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img 
          src={heroImage} 
          alt="Fitness transformation" 
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/85 to-background/70 lg:from-background/90 lg:via-background/80 lg:to-background/60"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background/50"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 w-full max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 xl:gap-10 items-center min-h-[60vh] py-4 lg:py-6">
          
          {/* Left Column - Text Content */}
          <div className="text-center lg:text-left animate-fade-in order-2 lg:order-1">
            <div className="max-w-2xl mx-auto lg:mx-0">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black mb-4 md:mb-6 leading-tight tracking-tight">
                {t('hero.title')}
              </h1>
              
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-muted-foreground mb-6 md:mb-8 leading-relaxed">
                {t('hero.subtitle')}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start items-center mb-8 md:mb-12">
                <Button 
                  size="lg"
                  className="hero-button group text-base md:text-lg px-8 md:px-10 py-4 md:py-6 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  onClick={scrollToFunnel}
                >
                  {t('hero.ctaPrimary')}
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                </Button>
              </div>
            </div>
          </div>

          {/* Right Column - Visual Element with Text Overlay */}
          <div className="relative order-1 lg:order-2 hidden lg:block">
            <div className="relative w-full h-56 sm:h-76 md:h-92 lg:h-[22rem] xl:h-[24rem] rounded-2xl lg:rounded-3xl overflow-hidden shadow-2xl group">
              <img
                src="/hero-alejandro.webp"
                alt="Fitness transformation showcase"
                className="w-full h-full object-cover object-[center_30%] scale-100 group-hover:scale-105 transition-transform duration-700 ease-out"
              />
              {/* Multiple overlay layers for depth */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-transparent to-transparent"></div>
              <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-black/30"></div>
              
              {/* Animated overlay effect */}
              <div className="absolute inset-0 bg-gradient-to-t from-primary/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              {/* Text Overlay - Hidden */}
              {/* <div className="absolute inset-0 flex flex-col justify-end p-4 sm:p-6 lg:p-8 text-white">
                <div className="space-y-3 sm:space-y-4 relative">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-lg -m-2"></div>
                  
                  <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold leading-tight relative z-10 drop-shadow-lg">
                    {t('hero.imageOverlay.title')}
                  </h3>
                  <p className="text-sm sm:text-base leading-relaxed opacity-90 max-w-sm relative z-10 drop-shadow-md">
                    {t('hero.imageOverlay.description')}
                  </p>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-6 text-xs sm:text-sm relative z-10">
                    <div className="flex items-center space-x-2 group/item">
                      <div className="w-2 h-2 bg-green-400 rounded-full flex-shrink-0 group-hover/item:scale-125 transition-transform duration-300"></div>
                      <span className="font-medium drop-shadow-sm">{t('hero.imageOverlay.provenResults')}</span>
                    </div>
                    <div className="flex items-center space-x-2 group/item">
                      <div className="w-2 h-2 bg-blue-400 rounded-full flex-shrink-0 group-hover/item:scale-125 transition-transform duration-300"></div>
                      <span className="font-medium drop-shadow-sm">{t('hero.imageOverlay.expertCoaching')}</span>
                    </div>
                  </div>
                </div>
              </div> */}
              
              {/* Subtle border effect */}
              <div className="absolute inset-0 rounded-2xl lg:rounded-3xl border border-white/10 pointer-events-none"></div>
            </div>
          </div>

        </div>

        {/* Benefits Section */}
        <div className="mt-8 lg:mt-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 animate-slide-up">
            <div className="text-center lg:text-left bg-background/50 backdrop-blur-sm rounded-xl p-6 border border-border/50 hover:bg-background/70 transition-all duration-300">
              <div className="text-lg sm:text-xl font-bold gradient-text mb-2">{t('hero.benefits.coaching.title')}</div>
              <div className="text-muted-foreground leading-relaxed text-sm sm:text-base">{t('hero.benefits.coaching.description')}</div>
            </div>
            <div className="text-center lg:text-left bg-background/50 backdrop-blur-sm rounded-xl p-6 border border-border/50 hover:bg-background/70 transition-all duration-300">
              <div className="text-lg sm:text-xl font-bold gradient-text mb-2">{t('hero.benefits.support.title')}</div>
              <div className="text-muted-foreground leading-relaxed text-sm sm:text-base">{t('hero.benefits.support.description')}</div>
            </div>
            <div className="text-center lg:text-left bg-background/50 backdrop-blur-sm rounded-xl p-6 border border-border/50 hover:bg-background/70 transition-all duration-300 sm:col-span-2 lg:col-span-1">
              <div className="text-lg sm:text-xl font-bold gradient-text mb-2">{t('hero.benefits.structure.title')}</div>
              <div className="text-muted-foreground leading-relaxed text-sm sm:text-base">{t('hero.benefits.structure.description')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Elements - Responsive */}
      <div className="absolute top-16 sm:top-20 right-4 sm:right-10 animate-float hidden sm:block">
        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary/20 backdrop-blur-sm"></div>
      </div>
      <div className="absolute bottom-20 sm:bottom-32 left-4 sm:left-10 animate-float hidden sm:block" style={{ animationDelay: '1s' }}>
        <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-primary/30 backdrop-blur-sm"></div>
      </div>
    </section>
  );
};

export default Hero;